import React, { useState, useRef, useEffect } from 'react';

interface AnnotationManagementProps {
    onAddAnnotation: (content: string) => void;
    setForceToolbarVisible: (isVisible: boolean) => void;
}

const AnnotationManagement: React.FC<AnnotationManagementProps> = ({ onAddAnnotation, setForceToolbarVisible }) => {
    const [isAddingAnnotation, setIsAddingAnnotation] = useState(false);
    const [annotationContent, setAnnotationContent] = useState('');
    const annotationFormRef = useRef<HTMLDivElement>(null);
    const savedSelection = useRef<Range | null>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (annotationFormRef.current && !annotationFormRef.current.contains(event.target as Node)) {
                setIsAddingAnnotation(false);
                setForceToolbarVisible(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [setForceToolbarVisible]);

    const handleAddAnnotationClick = () => {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            savedSelection.current = selection.getRangeAt(0);
        }
        setIsAddingAnnotation(true);
        setForceToolbarVisible(true);
    };

    const handleAnnotationSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (annotationContent.trim() && savedSelection.current) {
            // Restore selection
            const selection = window.getSelection();
            if (selection) {
                selection.removeAllRanges();
                selection.addRange(savedSelection.current);
            }

            onAddAnnotation(annotationContent);

            setIsAddingAnnotation(false);
            setAnnotationContent('');
            setForceToolbarVisible(false);
        }
    };

    return (
        <div className="relative flex items-center">
            <button
                onClick={handleAddAnnotationClick}
                className="px-2 py-1 hover:bg-mcdm-primary-200 rounded text-mcdm-third-500 text-sm"
                aria-label="Ajouter une annotation"
            >
                Annotation
            </button>

            {isAddingAnnotation && (
                <div ref={annotationFormRef} className="absolute z-50 bg-white border border-gray-300 rounded shadow-md mt-2 p-2 min-w-[250px]" style={{ top: '100%', left: 0 }}>
                    <form onSubmit={handleAnnotationSubmit} className="flex flex-col space-y-2">
                        <textarea
                            value={annotationContent}
                            onChange={(e) => setAnnotationContent(e.target.value)}
                            placeholder="Contenu de l'annotation"
                            rows={3}
                            className="border rounded p-1 text-sm text-mcdm-third-500 w-full resize-none focus:outline-none focus:ring-1 focus:ring-mcdm-secondary-500"
                            autoFocus
                        />
                        <div className="flex justify-between">
                            <button
                                type="button"
                                onClick={() => {
                                    setIsAddingAnnotation(false);
                                    setForceToolbarVisible(false);
                                }}
                                className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                className={`px-2 py-1 text-white text-xs rounded ${annotationContent.trim() ? 'bg-mcdm-green-300 hover:bg-mcdm-green-400' : 'bg-mcdm-green-300 opacity-50 cursor-not-allowed'}`}
                                disabled={!annotationContent.trim()}
                            >
                                Ajouter
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default AnnotationManagement;
